import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import logger from "@/utils/logger";

/**
 * ErrorBoundary — captura errores JS de los componentes hijos y muestra una UI
 * de fallback en lugar de tumbar toda la app. Reporta a Sentry si está disponible.
 *
 * Uso:
 *   <ErrorBoundary fallbackTitle="Error en vista X">
 *     <ComponenteRiesgoso />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    logger.error("[ErrorBoundary]", this.props.fallbackTitle || "error capturado", error, errorInfo);
    this.setState({ errorInfo });

    // Reportar a Sentry si está disponible (solo en producción)
    if (typeof window !== "undefined" && window.Sentry?.captureException) {
      try {
        window.Sentry.captureException(error, {
          tags: { boundary: this.props.fallbackTitle || "unknown" },
          extra: { componentStack: errorInfo?.componentStack },
        });
      } catch (_) {
        // Silencioso — no romper la UI por un fallo en el reporting
      }
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        className="p-6 m-4 rounded-lg border border-red-700/50 bg-red-950/30 text-red-100"
        data-testid="error-boundary-fallback"
      >
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-red-200 mb-1">
              {this.props.fallbackTitle || "Algo salió mal en esta sección"}
            </h3>
            <p className="text-sm text-red-300/90 mb-3">
              El resto de la aplicación sigue funcionando. Puedes intentar recargar esta sección
              o volver atrás.
            </p>
            {this.state.error && (
              <details className="text-xs bg-black/30 p-2 rounded mt-2">
                <summary className="cursor-pointer text-red-300 hover:text-red-200">
                  Detalles técnicos
                </summary>
                <pre className="mt-2 overflow-x-auto text-red-200/80 whitespace-pre-wrap break-words">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack?.slice(0, 800)}
                </pre>
              </details>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={this.handleRetry}
            size="sm"
            className="bg-red-700 hover:bg-red-600 text-white"
            data-testid="error-boundary-retry"
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            Reintentar
          </Button>
          {this.props.onReset && (
            <Button
              onClick={() => {
                this.handleRetry();
                this.props.onReset();
              }}
              size="sm"
              variant="outline"
              className="border-red-700 text-red-200 hover:bg-red-900/30"
              data-testid="error-boundary-reset"
            >
              Volver
            </Button>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
export { ErrorBoundary };
